import React, { useEffect, useState } from 'react';
import { XMarkIcon, PaperAirplaneIcon, CheckCircleIcon, ExclamationTriangleIcon, ClipboardDocumentIcon } from '@heroicons/react/24/outline';
import preopService from '../../services/preopService';
import { errText } from '../../services/scheduleService';
import PreopAnswers, { RELATIONSHIP } from './PreopAnswers';

export const PREOP_STATUS = {
  sent: { label: 'Pre-op sent', tone: 'bg-blue-100 text-blue-700' },
  in_progress: { label: 'Pre-op started', tone: 'bg-blue-100 text-blue-700' },
  expired: { label: 'Pre-op link expired', tone: 'bg-amber-100 text-amber-800' },
  completed: { label: 'Pre-op back', tone: 'bg-teal-100 text-teal-800' },
  reviewed: { label: 'Pre-op reviewed', tone: 'bg-green-100 text-green-700' },
  cancelled: { label: 'Pre-op cancelled', tone: 'bg-slate-100 text-slate-500' },
};
const WAITING = ['sent', 'in_progress', 'expired'];
const BACK = ['completed', 'reviewed'];
const fmtWhen = (d) => (d ? new Date(d).toLocaleString('en-ZA', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—');
const lastDay = (d) => new Date(new Date(d).getTime() - 1000).toLocaleDateString('en-ZA', { day: 'numeric', month: 'long' });

// "Pre-op back · 2 alerts" — for a booked patient's row or a list.
export const PreopBadge = ({ preop }) => {
  const s = PREOP_STATUS[preop.status] || { label: preop.status, tone: 'bg-slate-100 text-slate-600' };
  const alerts = BACK.includes(preop.status) ? preop.alert_count || 0 : 0;
  const tone = preop.status === 'completed' && alerts ? 'bg-red-100 text-red-700' : s.tone;
  return <span className={`whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-semibold ${tone}`}>{s.label}{alerts ? ` · ${alerts} alert${alerts === 1 ? '' : 's'}` : ''}</span>;
};

/**
 * PreopPanel — the pre-op questionnaire for one patient on one booking: send it,
 * follow it up (new link, cancel), and read what came back. The anaesthetist
 * marks it reviewed — usually on the phone, but here too.
 *   patient: { patient_id, name, email }
 *   bookingLabel: "Mon 14 Sep · Milpark · Theatre 2 · Dr Pillay"
 *   onChanged(): the booking's questionnaire status changed
 */
const PreopPanel = ({ bid, patient, bookingLabel, onClose, onChanged }) => {
  const [q, setQ] = useState(null);
  const [mode, setMode] = useState('loading'); // loading | none | view
  const [email, setEmail] = useState(patient.email || '');
  const [sent, setSent] = useState(null); // { link, emailed, email_error }
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');
  const [copied, setCopied] = useState(false);

  // The latest questionnaire for this patient on this booking.
  useEffect(() => {
    preopService.list({ bid, patientId: patient.patient_id })
      .then(async ({ questionnaires }) => {
        const latest = questionnaires.sort((a, b) => b.preop_id - a.preop_id)[0];
        if (!latest) { setMode('none'); return; }
        setQ(await preopService.get(latest.preop_id));
        setMode('view');
      })
      .catch((e) => { setErr(errText(e)); setMode('none'); });
  }, [bid, patient.patient_id]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !busy) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, busy]);

  const run = async (what, fn) => {
    setBusy(what);
    setErr('');
    try {
      await fn();
    } catch (e) {
      setErr(errText(e));
    } finally {
      setBusy('');
    }
  };

  const sendNew = () => run('send', async () => {
    const out = await preopService.send({ bid, patientId: patient.patient_id, email });
    setQ(out.questionnaire);
    setSent(out);
    setMode('view');
    onChanged?.();
  });
  const resend = () => run('send', async () => {
    const out = await preopService.resend(q.preop_id, email);
    setQ({ ...q, ...out.questionnaire });
    setSent(out);
    onChanged?.();
  });
  const cancel = () => run('cancel', async () => { setQ({ ...q, ...(await preopService.cancel(q.preop_id)) }); setSent(null); onChanged?.(); });
  const review = () => run('review', async () => { setQ(await preopService.review(q.preop_id, note.trim() || undefined)); onChanged?.(); });

  const copy = async () => {
    try { await navigator.clipboard.writeText(sent.link); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch { /* select it by hand */ }
  };

  const field = 'w-full rounded-lg border border-slate-300 px-2.5 py-2 text-sm focus:border-brand-teal focus:outline-none focus:ring-2 focus:ring-brand-teal/30';
  const emailField = (
    <label className="block text-xs font-semibold text-slate-600">Send to
      <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="No email on file — you’ll get a link to share" className={`mt-1 ${field}`} />
    </label>
  );

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/40 p-4" onMouseDown={(e) => e.target === e.currentTarget && !busy && onClose()}>
      <div className="flex max-h-[92vh] w-full max-w-2xl flex-col overflow-hidden rounded-xl bg-white shadow-xl">
        <div className="flex items-start justify-between gap-3 border-b border-slate-100 px-5 py-4">
          <div className="min-w-0">
            <h2 className="text-lg font-bold text-brand-deep">Pre-op questionnaire · {patient.name}</h2>
            <p className="truncate text-xs text-slate-500">{bookingLabel}</p>
          </div>
          <div className="flex items-center gap-2">
            {q && <PreopBadge preop={q} />}
            <button onClick={onClose} disabled={!!busy} className="rounded-md p-1 text-slate-400 hover:bg-slate-100"><XMarkIcon className="h-5 w-5" /></button>
          </div>
        </div>

        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-5 py-4 text-sm">
          {mode === 'loading' && <p className="text-slate-400">Loading…</p>}

          {mode === 'none' && (
            <>
              <p className="text-slate-600">
                The patient gets a link to health questions for their anaesthetist — previous anaesthetics, heart and breathing, medicines, allergies and so on. They can save and come back to it. When they send it, the anaesthetist is told on their phone, with anything that needs a look flagged.
              </p>
              {emailField}
            </>
          )}

          {mode === 'view' && q && (
            <>
              {sent && (
                <div className={`rounded-lg border px-3 py-2.5 ${sent.emailed ? 'border-green-200 bg-green-50 text-green-800' : 'border-amber-200 bg-amber-50 text-amber-900'}`}>
                  <p className="flex items-center gap-1.5 font-semibold">
                    {sent.emailed ? <CheckCircleIcon className="h-5 w-5" /> : <ExclamationTriangleIcon className="h-5 w-5" />}
                    {sent.emailed ? `Emailed to ${sent.questionnaire.sent_to}.` : sent.email_error}
                  </p>
                  <div className="mt-2 flex items-center gap-2">
                    <input readOnly value={sent.link} onFocus={(e) => e.target.select()} className="min-w-0 flex-1 rounded-md border border-slate-300 bg-white px-2 py-1 font-mono text-xs text-slate-700" />
                    <button onClick={copy} className="inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white px-2 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50">
                      <ClipboardDocumentIcon className="h-4 w-4" /> {copied ? 'Copied' : 'Copy link'}
                    </button>
                  </div>
                  <p className="mt-1 text-xs">You can also send this link by WhatsApp or SMS. It works until the patient sends their answers, up to and including {lastDay(sent.questionnaire.expires_at)}.</p>
                </div>
              )}

              {(WAITING.includes(q.status) || q.status === 'cancelled') && (
                <>
                  <dl className="grid grid-cols-2 gap-x-4 gap-y-2 rounded-lg border border-slate-200 px-3 py-2.5 text-xs">
                    <div><dt className="text-slate-400">Sent</dt><dd className="font-semibold text-slate-700">{q.sent_at ? `${fmtWhen(q.sent_at)}${q.sent_to ? ` to ${q.sent_to}` : ''}` : '—'}</dd></div>
                    <div><dt className="text-slate-400">Opened by the patient</dt><dd className="font-semibold text-slate-700">{q.opened_at ? fmtWhen(q.opened_at) : 'Not yet'}</dd></div>
                    <div><dt className="text-slate-400">Answers saved so far</dt><dd className="font-semibold text-slate-700">{q.last_saved_at ? fmtWhen(q.last_saved_at) : 'None yet'}</dd></div>
                    <div><dt className="text-slate-400">Link works until</dt><dd className="font-semibold text-slate-700">{q.expires_at ? lastDay(q.expires_at) : '—'}</dd></div>
                  </dl>
                  {q.status === 'expired' && <p className="text-amber-800">The link expired before the patient sent their answers. Send a new link — anything they saved is kept.</p>}
                  {q.status === 'cancelled' && <p className="text-slate-500">This questionnaire was cancelled.</p>}
                  {emailField}
                </>
              )}

              {BACK.includes(q.status) && (
                <>
                  <p className="text-xs text-slate-500">
                    Sent back {fmtWhen(q.completed_at)} by {q.completed_by_name}{q.completed_by_relationship && q.completed_by_relationship !== 'self' ? ` (${RELATIONSHIP[q.completed_by_relationship]})` : ''}.
                  </p>
                  <PreopAnswers q={q} />
                  {q.status === 'reviewed' ? (
                    <div className="rounded-lg border border-green-200 bg-green-50 px-3 py-2.5 text-green-900">
                      <p className="font-semibold">Reviewed by {q.reviewed_by_name} · {fmtWhen(q.reviewed_at)}</p>
                      {q.review_note && <p className="mt-0.5">{q.review_note}</p>}
                    </div>
                  ) : q.permissions?.can_review ? (
                    <div className="space-y-2 rounded-lg border border-slate-200 px-3 py-2.5">
                      <label className="block text-xs font-semibold text-slate-600">Your note (optional)
                        <textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Stop warfarin 5 days before; latex-free theatre." className={`mt-1 resize-none ${field}`} />
                      </label>
                      <div className="flex justify-end">
                        <button onClick={review} disabled={!!busy} className="inline-flex items-center gap-1.5 rounded-lg bg-brand-navy px-3 py-2 text-sm font-semibold text-white disabled:opacity-60">
                          <CheckCircleIcon className="h-4 w-4" /> {busy === 'review' ? 'Saving…' : 'Mark reviewed'}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <p className="rounded-lg bg-slate-50 px-3 py-2 text-slate-600">Waiting for {q.doctor} to review it — they’ve been told on their phone.</p>
                  )}
                </>
              )}
            </>
          )}

          {err && <p className="text-sm text-red-600">{err}</p>}
        </div>

        <div className="flex flex-wrap items-center justify-end gap-2 border-t border-slate-100 px-5 py-3">
          <button onClick={onClose} disabled={!!busy} className="rounded-lg px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-100">Close</button>
          {mode === 'view' && q && WAITING.includes(q.status) && (
            <>
              <button onClick={cancel} disabled={!!busy} className="rounded-lg px-3 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-100">{busy === 'cancel' ? 'Cancelling…' : 'Cancel questionnaire'}</button>
              <button onClick={resend} disabled={!!busy} className="inline-flex items-center gap-1.5 rounded-lg bg-brand-navy px-3 py-2 text-sm font-semibold text-white disabled:opacity-60">
                <PaperAirplaneIcon className="h-4 w-4" /> {busy === 'send' ? 'Sending…' : 'Send a new link'}
              </button>
            </>
          )}
          {mode === 'view' && q && BACK.includes(q.status) && (
            <button onClick={() => { setQ(null); setSent(null); setMode('none'); }} disabled={!!busy} title="If the patient’s health has changed — this one stays on record" className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">
              Send a new questionnaire…
            </button>
          )}
          {(mode === 'none' || (mode === 'view' && q?.status === 'cancelled')) && (
            <button onClick={sendNew} disabled={!!busy} className="inline-flex items-center gap-1.5 rounded-lg bg-brand-gradient px-4 py-2 text-sm font-semibold text-white hover:opacity-95 disabled:opacity-50">
              <PaperAirplaneIcon className="h-4 w-4" /> {busy === 'send' ? 'Sending…' : 'Send to patient'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default PreopPanel;
